import { Link } from "react-router-dom";
import { 
  Wrench, 
  Hammer, 
  Cog, 
  Smartphone,
  CheckCircle2,
  FileText,
  Camera,
  ClipboardList,
  ArrowRight,
  MessageCircle,
  AlertCircle,
  Settings,
  Search,
  Shield,
  Armchair,
  Activity,
  Radiation,
  Gauge,
  Waves
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const WHATSAPP_NUMBER = "56929894282";
const WHATSAPP_SERVICES_MESSAGE = encodeURIComponent("Hola, quiero cotizar MP/MC para mi equipo. Equipo: ___, Ciudad: ___.");
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_SERVICES_MESSAGE}`;

const services = [
  {
    id: "preventiva",
    icon: Wrench,
    title: "Mantención Preventiva (MP)",
    description: "Planifica y evita fallas. Revisamos, ajustamos y dejamos registro completo de cada intervención.",
    scope: [
      "Inspección completa del equipo",
      "Calibración básica de componentes",
      "Limpieza técnica según tipo de equipo",
      "Verificación de seguridad eléctrica y mecánica",
      "Checklist de mantención estandarizado"
    ],
    deliverables: [
      { icon: FileText, text: "Informe de mantención detallado" },
      { icon: ClipboardList, text: "Recomendaciones técnicas" },
      { icon: ArrowRight, text: "Próximos pasos y frecuencia sugerida" },
      { icon: Settings, text: "Registro en historial del equipo" }
    ],
    color: "#0FB4B7"
  },
  {
    id: "correctiva",
    icon: Hammer,
    title: "Mantención Correctiva (MC)",
    description: "Atendemos fallas y recuperamos la operación con diagnóstico preciso y reparación profesional.",
    scope: [
      "Diagnóstico de falla con evaluación técnica",
      "Reparación especializada de componentes",
      "Pruebas de funcionamiento post-reparación",
      "Recomendación de repuestos si aplica",
      "Verificación de seguridad"
    ],
    deliverables: [
      { icon: FileText, text: "Orden de trabajo documentada" },
      { icon: AlertCircle, text: "Informe con causa probable y acciones" },
      { icon: Camera, text: "Evidencia fotográfica (si aplica)" },
      { icon: Shield, text: "Garantía del trabajo realizado" }
    ],
    color: "#384091"
  },
  {
    id: "repuestos",
    icon: Cog,
    title: "Repuestos",
    description: "Cotizamos y suministramos repuestos compatibles según marca y modelo de tu equipo.",
    scope: [
      "Búsqueda de repuestos originales y compatibles",
      "Evaluación de alternativas técnicas",
      "Recomendación según relación calidad-precio",
      "Verificación de disponibilidad y tiempos",
      "Asesoría técnica para instalación"
    ],
    deliverables: [],
    cta: {
      text: "Solicitar cotización de repuesto",
      link: "/contacto?asunto=Repuestos",
      icon: Search
    },
    color: "#0FB4B7"
  },
  {
    id: "seguimiento",
    icon: Smartphone,
    title: "Seguimiento y Trazabilidad",
    description: "Historial centralizado, reportes y control de mantenciones para asegurar continuidad operacional.",
    scope: [
      "Historial completo por equipo",
      "Documentos y evidencias centralizados",
      "Alertas de mantención preventiva",
      "Reportes de estado y seguimiento",
      "Acceso a información desde cualquier lugar"
    ],
    deliverables: [],
    cta: {
      text: "Conocer la app gratuita",
      link: "/app",
      icon: ArrowRight
    },
    color: "#384091"
  }
];

const equipmentList = [
  { name: "Sillón dental", icon: Armchair },
  { name: "Autoclave", icon: Activity },
  { name: "Rayos X", icon: Radiation },
  { name: "Compresor", icon: Gauge },
  { name: "Ultrasonido", icon: Waves },
  { name: "Otros equipos", icon: Wrench }
];

export default function Servicios() {
  return (
    <div data-testid="servicios-page">
      {/* Hero Section */}
      <section className="bg-[#384091] py-16 md:py-20" data-testid="servicios-hero">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              Nuestros Servicios
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6">
              Mantención preventiva, correctiva, repuestos y seguimiento para equipos clínicos con trazabilidad completa y documentación profesional.
            </p>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                size="lg"
                className="bg-[#25D366] hover:bg-[#25D366]/90 text-white h-12 px-6 font-medium"
                data-testid="servicios-hero-cta"
              >
                <MessageCircle className="h-5 w-5 mr-2" />
                Cotizar por WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-20 bg-[#F5F7FA]" data-testid="servicios-grid">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="space-y-8">
            {services.map((service, index) => (
              <Card 
                key={service.id} 
                id={service.id}
                className="bg-white border-0 shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden"
                data-testid={`service-detail-${service.id}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-12">
                  {/* Service Header */}
                  <div 
                    className="lg:col-span-4 p-6 md:p-8"
                    style={{ backgroundColor: `${service.color}08` }}
                  >
                    <div 
                      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                      style={{ backgroundColor: `${service.color}15` }}
                    >
                      <service.icon className="h-7 w-7" style={{ color: service.color }} />
                    </div>
                    <h2 className="font-heading text-xl md:text-2xl font-semibold text-[#1F2937] mb-3">
                      {service.title}
                    </h2>
                    <p className="text-[#4B5563] leading-relaxed">
                      {service.description}
                    </p>
                  </div>

                  {/* Scope */}
                  <CardContent className="lg:col-span-4 p-6 md:p-8 lg:border-l border-gray-100">
                    <h3 className="font-heading font-semibold text-base text-[#1F2937] mb-4 flex items-center gap-2">
                      <ClipboardList className="h-5 w-5" style={{ color: service.color }} />
                      Alcance del servicio
                    </h3>
                    <ul className="space-y-3">
                      {service.scope.map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle2 
                            className="h-5 w-5 flex-shrink-0 mt-0.5" 
                            style={{ color: service.color }} 
                          />
                          <span className="text-[#4B5563] text-sm leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>

                  {/* Deliverables / CTA */}
                  <CardContent className="lg:col-span-4 p-6 md:p-8 lg:border-l border-gray-100 bg-gray-50/50">
                    {service.deliverables.length > 0 ? (
                      <>
                        <h3 className="font-heading font-semibold text-base text-[#1F2937] mb-4 flex items-center gap-2">
                          <FileText className="h-5 w-5" style={{ color: service.color }} />
                          Entregables
                        </h3>
                        <ul className="space-y-3">
                          {service.deliverables.map((item, i) => (
                            <li key={i} className="flex items-center gap-3">
                              <div 
                                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: `${service.color}10` }}
                              >
                                <item.icon className="h-4 w-4" style={{ color: service.color }} />
                              </div>
                              <span className="text-[#4B5563] text-sm">{item.text}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : service.cta ? (
                      <div className="h-full flex flex-col justify-center">
                        <p className="text-[#4B5563] mb-6 leading-relaxed">
                          {service.id === 'repuestos' 
                            ? '¿Necesitas un repuesto específico? Envíanos los datos de tu equipo y te cotizamos.'
                            : 'Gestiona tus equipos con nuestra app gratuita. Registro, historial y alertas en un solo lugar.'
                          }
                        </p>
                        <Link to={service.cta.link}>
                          <Button 
                            className="w-full md:w-auto font-medium"
                            style={{ backgroundColor: service.color }}
                            data-testid={`service-cta-${service.id}`}
                          >
                            {service.cta.icon && <service.cta.icon className="h-4 w-4 mr-2" />}
                            {service.cta.text}
                          </Button>
                        </Link>
                      </div>
                    ) : null}
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment List */}
      <section className="py-16 md:py-20 bg-white" data-testid="equipment-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1F2937] mb-3">
              ¿Qué equipos trabajamos?
            </h2>
            <p className="text-[#4B5563] max-w-2xl mx-auto">
              Atendemos una amplia variedad de equipamiento clínico y dental
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {equipmentList.map((equipment, index) => (
              <Card 
                key={index} 
                className="bg-[#F5F7FA] border-0 hover:bg-[#0FB4B7]/5 hover:shadow-md transition-all duration-300 cursor-default"
                data-testid={`equipment-item-${index}`}
              >
                <CardContent className="p-5 text-center">
                  <div className="w-11 h-11 mx-auto rounded-xl bg-[#0FB4B7]/10 flex items-center justify-center mb-3">
                    <equipment.icon className="h-[22px] w-[22px] text-[#0FB4B7]" />
                  </div>
                  <p className="font-medium text-[#1F2937] text-sm">
                    {equipment.name}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-[#0FB4B7]" data-testid="servicios-cta">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4">
            ¿Listo para cotizar mantención?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Escríbenos con los datos de tu equipo y te enviamos una cotización sin compromiso
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                size="lg" 
                className="bg-[#25D366] hover:bg-[#25D366]/90 text-white h-12 px-8 font-medium"
                data-testid="servicios-cta-whatsapp"
              >
                <MessageCircle className="h-5 w-5 mr-2" />
                Cotizar por WhatsApp
              </Button>
            </a>
            <Link to="/contacto">
              <Button 
                size="lg" 
                variant="outline" 
                className="bg-white/10 border-white text-white hover:bg-white hover:text-[#0FB4B7] h-12 px-8 font-medium"
                data-testid="servicios-cta-contact"
              >
                Ir al formulario
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
